import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO
        title="Cookie Policy"
        description="Cookie policy for Energy Performance Direct. Learn how we use cookies on our website."
        canonical="/cookies"
        noindex={true}
      />
      <Navbar />
      <section className="text-white py-12" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
              <Cookie className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="cookie-policy-heading">
              Cookie Policy
            </h1>
            <p className="text-lg opacity-95">How We Use Cookies</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-none shadow-lg mb-8">
              <CardContent className="p-8">
                <div className="prose prose-green max-w-none">
                  
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      What Are Cookies?
                    </h2>
                    <p className="text-gray-700">
                      Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
                    </p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      Types of Cookies We Use
                    </h2>
                    
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">Essential Cookies</h3>
                        <p className="text-gray-700 mb-2">These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility.</p>
                        <p className="text-sm text-gray-600 italic">These cannot be disabled as they are essential for the site to work.</p>
                      </div>

                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h3>
                        <p className="text-gray-700 mb-2">We use Google Analytics to understand how visitors use our website. This helps us improve our services and user experience.</p>
                        <p className="text-sm text-gray-600">Information collected: Pages visited, time spent on site, browser type, device type, IP address (masked).</p>
                      </div>

                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">Functional Cookies</h3>
                        <p className="text-gray-700">These cookies allow the website to remember choices you make (such as your preferred language or region) and provide enhanced, more personal features.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      Your Cookie Choices
                    </h2>
                    <p className="text-gray-700 mb-4">
                      When you first visit our website, you will see a cookie consent banner that allows you to:
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2">
                      <li><strong>Accept All:</strong> Allow all cookies for the best experience</li>
                      <li><strong>Reject Non-Essential:</strong> Only essential cookies will be used</li>
                      <li><strong>Customise:</strong> Choose which types of cookies you want to allow</li>
                    </ul>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      Managing Cookies
                    </h2>
                    <p className="text-gray-700 mb-4">
                      You can manage your cookie preferences at any time through your browser settings. Most browsers allow you to:
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2">
                      <li>See what cookies are stored and delete them individually</li>
                      <li>Block third-party cookies</li>
                      <li>Block cookies from specific websites</li>
                      <li>Delete all cookies when you close your browser</li>
                    </ul>
                    <p className="text-gray-700 mt-4">
                      Please note that deleting or blocking cookies may impact your user experience and some features of the website may not function properly.
                    </p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      Third-Party Cookies
                    </h2>
                    <p className="text-gray-700 mb-3">
                      We use the following third-party services that may set cookies:
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2">
                      <li><strong>Google Analytics:</strong> For website analytics and understanding user behaviour</li>
                      <li><strong>PayPal:</strong> For processing payments (only on payment pages)</li>
                    </ul>
                    <p className="text-gray-700 mt-4">
                      These third parties have their own privacy policies and cookie policies, which we recommend you review.
                    </p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      Cookie Retention
                    </h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                        <li><strong>Persistent Cookies:</strong> Remain on your device for up to 26 months</li>
                        <li><strong>Analytics Cookies:</strong> Retained for 12-26 months</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
                      Updates to This Policy
                    </h2>
                    <p className="text-gray-700">
                      We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Questions About Cookies?</h3>
                    <p className="text-gray-700 mb-2">
                      If you have any questions about our use of cookies, please contact us:
                    </p>
                    <p className="text-gray-700">
                      Email: <a href="mailto:info@energyperformancedirect.co.uk" className="text-green-600 hover:underline">info@energyperformancedirect.co.uk</a><br />
                      Tel: <a href="tel:01392980077" className="text-green-600 hover:underline">01392 980077</a>
                    </p>
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* Related Links */}
            <div className="flex gap-4 justify-center">
              <Link to="/privacy" className="text-green-600 hover:underline font-medium">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-green-600 hover:underline font-medium">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
